// src/components/kit/epk/themes.ts
//
// Layout presets, color palettes, font themes, and other constants
// for the EPK feature. Adapted from the EPK mockup studio.

import type {
  EpkLayout,
  EpkPaletteId,
  EpkFontThemeId,
  EpkColors,
  EpkFormState,
} from '@/types/epk';

// ============================================================
// Layout Presets
// ============================================================

export interface EpkLayoutConfig {
  id: EpkLayout;
  name: string;
  description: string;
}

export const EPK_LAYOUTS: Record<EpkLayout, EpkLayoutConfig> = {
  headline: {
    id: 'headline',
    name: 'Headline',
    description: 'Single stacked column, full-bleed hero',
  },
  dossier: {
    id: 'dossier',
    name: 'Dossier',
    description: 'Sticky sidebar with content column',
  },
  bulletin: {
    id: 'bulletin',
    name: 'Bulletin',
    description: 'Asymmetric magazine grid',
  },
  reel: {
    id: 'reel',
    name: 'Reel',
    description: 'Split hero, media-forward',
  },
};

// ============================================================
// Color Palettes
// ============================================================

export interface EpkPaletteConfig {
  id: EpkPaletteId;
  name: string;
  description: string;
  colors: EpkColors;
}

export const EPK_PALETTES: Record<Exclude<EpkPaletteId, 'custom'>, EpkPaletteConfig> = {
  marquee: {
    id: 'marquee',
    name: 'Marquee',
    description: 'Vintage theater, burgundy & gold',
    colors: {
      background: '#17130F',
      surface: '#211C17',
      surfaceAlt: '#2A241D',
      text: '#F2EDE4',
      textMuted: '#B0A497',
      accent: '#D6A94A',
      accent2: '#8B2635',
      border: '#332C23',
      heroText: '#FFFFFF',
    },
  },
  voltage: {
    id: 'voltage',
    name: 'Voltage',
    description: 'Club-dark, electric blue & lime',
    colors: {
      background: '#0B0B12',
      surface: '#15141F',
      surfaceAlt: '#1D1B2B',
      text: '#F2F0FF',
      textMuted: '#8B87A6',
      accent: '#4C6FFF',
      accent2: '#C6FF3D',
      border: '#2A2740',
      heroText: '#FFFFFF',
    },
  },
  sundial: {
    id: 'sundial',
    name: 'Sundial',
    description: 'Sun-worn paper, olive & rust',
    colors: {
      background: '#F1EEE7',
      surface: '#FAF8F3',
      surfaceAlt: '#E7E2D6',
      text: '#2A2620',
      textMuted: '#6E6659',
      accent: '#338439',
      accent2: '#B5652D',
      border: '#DDD6C6',
      heroText: '#FFFFFF',
    },
  },
  newsprint: {
    id: 'newsprint',
    name: 'Newsprint',
    description: 'Broadsheet mono, red accent',
    colors: {
      background: '#EDEDE8',
      surface: '#FFFFFF',
      surfaceAlt: '#E2E2DC',
      text: '#171717',
      textMuted: '#5B5B5B',
      accent: '#C0392B',
      accent2: '#171717',
      border: '#D6D6CE',
      heroText: '#FFFFFF',
    },
  },
  duskpop: {
    id: 'duskpop',
    name: 'Dusk Pop',
    description: 'Soft lavender, magenta & plum',
    colors: {
      background: '#F1EFF2',
      surface: '#FAF9FB',
      surfaceAlt: '#E6E3EA',
      text: '#221F29',
      textMuted: '#6A6575',
      accent: '#4C6FFF',
      accent2: '#5B3E8C',
      border: '#E0DDE6',
      heroText: '#FFFFFF',
    },
  },
};

// ============================================================
// Font Themes
// ============================================================

export interface EpkFontThemeConfig {
  id: EpkFontThemeId;
  name: string;
  displayFont: string;
  bodyFont: string;
  googleImport: string;
  /** CSS font-style for display headings (e.g. 'italic' for editorial) */
  displayStyle: string;
  displayWeight: string;
  displayTracking: string;
}

export const EPK_FONT_THEMES: Record<Exclude<EpkFontThemeId, 'custom'>, EpkFontThemeConfig> = {
  editorial: {
    id: 'editorial',
    name: 'Editorial Serif',
    displayFont: 'Newsreader',
    bodyFont: 'Work Sans',
    googleImport: 'Newsreader:ital,wght@0,400;0,500;1,500&family=Work+Sans:wght@400;500;600',
    displayStyle: 'italic',
    displayWeight: '500',
    displayTracking: '0',
  },
  grotesk: {
    id: 'grotesk',
    name: 'Grotesk Bold',
    displayFont: 'Archivo Black',
    bodyFont: 'Inter',
    googleImport: 'Archivo+Black&family=Inter:wght@400;500;600;700',
    displayStyle: 'normal',
    displayWeight: '400',
    displayTracking: '-0.01em',
  },
  mono: {
    id: 'mono',
    name: 'Mono Tech',
    displayFont: 'Space Mono',
    bodyFont: 'Space Mono',
    googleImport: 'Space+Mono:wght@400;700',
    displayStyle: 'normal',
    displayWeight: '700',
    displayTracking: '-0.02em',
  },
  groove: {
    id: 'groove',
    name: 'Groove Poster',
    displayFont: 'Bungee',
    bodyFont: 'Karla',
    googleImport: 'Bungee&family=Karla:wght@400;500;600;700',
    displayStyle: 'normal',
    displayWeight: '400',
    displayTracking: '0',
  },
  soft: {
    id: 'soft',
    name: 'Soft Round',
    displayFont: 'Quicksand',
    bodyFont: 'Nunito Sans',
    googleImport: 'Quicksand:wght@600;700&family=Nunito+Sans:wght@400;600;800',
    displayStyle: 'normal',
    displayWeight: '700',
    displayTracking: '0',
  },
};

// ============================================================
// Available Google Fonts (for custom override dropdowns)
// ============================================================

export const EPK_AVAILABLE_FONTS: string[] = [
  'Newsreader',
  'Work Sans',
  'Archivo Black',
  'Inter',
  'Space Mono',
  'Bungee',
  'Karla',
  'Quicksand',
  'Nunito Sans',
  'Playfair Display',
  'Source Sans 3',
  'Oswald',
  'Lato',
  'Cormorant Garamond',
  'Nunito',
  'Poppins',
  'DM Serif Display',
  'DM Sans',
  'Montserrat',
  'Raleway',
  'Merriweather',
  'Roboto',
  'Open Sans',
  'Bebas Neue',
  'Archivo',
  'Space Grotesk',
];

// ============================================================
// Social Platforms
// ============================================================

export interface SocialPlatformConfig {
  id: string;
  label: string;
}

export const SOCIAL_PLATFORMS: SocialPlatformConfig[] = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'bandcamp', label: 'Bandcamp' },
  { id: 'twitter', label: 'X / Twitter' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'spotify', label: 'Spotify' },
  { id: 'apple-music', label: 'Apple Music' },
  { id: 'soundcloud', label: 'SoundCloud' },
  { id: 'website', label: 'Website' },
  { id: 'other', label: 'Other' },
];

// ============================================================
// Contact Roles
// ============================================================

export interface ContactRoleConfig {
  id: string;
  label: string;
}

export const CONTACT_ROLES: ContactRoleConfig[] = [
  { id: 'booking', label: 'Booking' },
  { id: 'management', label: 'Management' },
  { id: 'press', label: 'Press' },
  { id: 'general', label: 'General' },
];

// ============================================================
// Release Types
// ============================================================

export interface ReleaseTypeConfig {
  id: string;
  label: string;
}

export const RELEASE_TYPES: ReleaseTypeConfig[] = [
  { id: 'album', label: 'Album' },
  { id: 'ep', label: 'EP' },
  { id: 'single', label: 'Single' },
];

// ============================================================
// Release Link Platforms
// ============================================================

export interface ReleaseLinkPlatformConfig {
  id: string;
  label: string;
}

export const RELEASE_LINK_PLATFORMS: ReleaseLinkPlatformConfig[] = [
  { id: 'spotify', label: 'Spotify' },
  { id: 'apple-music', label: 'Apple Music' },
  { id: 'bandcamp', label: 'Bandcamp' },
  { id: 'soundcloud', label: 'SoundCloud' },
  { id: 'youtube-music', label: 'YouTube Music' },
  { id: 'tidal', label: 'Tidal' },
  { id: 'amazon-music', label: 'Amazon Music' },
  { id: 'other', label: 'Other' },
];

// ============================================================
// Default Form State
// ============================================================

export function createDefaultEpkFormState(): EpkFormState {
  return {
    slug: '',
    artist_name: '',
    tagline: '',
    bio: '',
    genre: '',
    location: '',
    for_fans_of: '',
    video_url: '',
    video_caption: '',
    releases: [],
    gallery_images: [],
    press_quotes: [],
    tour_dates: [],
    contacts: [],
    social_links: [],
    layout: 'headline',
    palette_id: 'marquee',
    colors: { ...EPK_PALETTES.marquee.colors },
    font_theme_id: 'editorial',
    fonts: {
      displayFont: 'Newsreader',
      bodyFont: 'Work Sans',
    },
    hero_image_key: null,
    hero_image_url: null,
    hero_image_fit: 'cover',
    hero_image_position: 'center',
    show_hero_artist_name: true,
    show_facts: true,
    show_bio: true,
    show_music: true,
    show_video: false,
    show_gallery: false,
    show_press: false,
    show_tour: false,
    show_contact: true,
    listen_url: '',
    booking_url: '',
    published: false,
  };
}
